import React, { useState } from 'react';
import { useGenerateSoleFromObjectsMutation, useSoleMutation, usePipelineMutation } from 'app/createApi';
import { useAppSelector, useAppDispatch } from 'app/hooks';
import { selectProjectPath, selectBranch } from 'app/sharedSlice';
import { selectSnowflakeAccount, selectSelectedDatabase } from 'app/snowflakeSlice';
import {
  selectSelectedDatabases,
  selectSelectedSchemas,
  selectSelectedSchemaLevelObjects,
  selectFlow,
  selectDataProductName,
  selectEnvironmentManagement,
  setGenerateSoleFromObjectsOutput,
  setSoleOutput,
} from '../reducers/builderSlice';
import StepWrapper from 'components/StepWrapper';
import LoadingAndErrorSection from 'components/LoadingAndErrorSection';
import SourceSelector from '../components/SourceSelector';
import { sendMetrics, MetricType, ExitTo } from 'utils/metrics';

export interface DatabaseTablesStepProps {
  onBack: () => void;
  onContinue: () => void;
}

export default function DatabaseTablesStep(props: DatabaseTablesStepProps): JSX.Element {
  const dispatch = useAppDispatch();
  const [generateSoleFromObjects] = useGenerateSoleFromObjectsMutation();
  const [sole] = useSoleMutation();
  const snowflakeAccount = useAppSelector(selectSnowflakeAccount);
  const selectedDatabase = useAppSelector(selectSelectedDatabase);
  const selectedDatabases = useAppSelector(selectSelectedDatabases);
  const selectedSchemas = useAppSelector(selectSelectedSchemas);
  const selectedSchemaLevelObjects = useAppSelector(selectSelectedSchemaLevelObjects);
  const flow = useAppSelector(selectFlow);
  const dataProductName = useAppSelector(selectDataProductName);
  const projectPath = useAppSelector(selectProjectPath);
  const branch = useAppSelector(selectBranch);
  const environmentManagement = useAppSelector(selectEnvironmentManagement);
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('Failed to generate config');

  const [pipeline] = usePipelineMutation();

  const pathForLink = projectPath.toLowerCase().split(' ').join('-');
  const devReadyLink = `https://develop.dataops.live/quickstart?autostart=true#LAUNCH_MODE=DATA_PRODUCT_CREATOR/https://app.dataops.live/${pathForLink}/-/tree/${branch}/`;

  const onContinue = (finishEarly: boolean): void => {
    setLoading(true);
    setFailed(false);
    const databaseToSend = selectedDatabases.map((database) => database.database);
    const schemasToSend = selectedSchemas.map((schema) => {
      return { database: schema.database, object_id: schema.schema };
    });
    const tablesToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'table')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const viewsToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'view')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const functionsToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'function')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const proceduresToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'procedure')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const fileFormatsToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'file_format')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const sequencesToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'sequence')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    const pipesToSend = selectedSchemaLevelObjects
      .filter((obj) => obj.type === 'pipe')
      .map((obj) => {
        return { database: obj.database, schema: obj.schema, object_id: obj.name };
      });
    generateSoleFromObjects({
      account: snowflakeAccount,
      databases: databaseToSend,
      schemas: schemasToSend,
      tables: tablesToSend,
      views: viewsToSend,
      functions: functionsToSend,
      procedures: proceduresToSend,
      file_formats: fileFormatsToSend,
      sequences: sequencesToSend,
      pipes: pipesToSend,
    })
      .unwrap()
      .then((res: any) => {
        const dbtSole = res;
        dispatch(setGenerateSoleFromObjectsOutput(dbtSole));
        sole({
          dbtSole,
          manageSole: environmentManagement === 'managed' ? dbtSole : null,
          toIncludeModels: true,
          toWriteConfigs: true,
          toWriteJobs: true,
          dataProductName,
          projectName: projectPath,
          account: snowflakeAccount,
          branch,
        })
          .unwrap()
          .then((soleRes: any) => {
            dispatch(setSoleOutput(soleRes));
            if (finishEarly) {
              pipeline({ dataProductName, projectName: projectPath, branch, mergeMain: flow === 'ddl' })
                .unwrap()
                .then((res: any) => {
                  console.log(res);
                })
                .catch((err) => {
                  console.log(err);
                  setErrorMessage(err.data);
                });
              sendMetrics(MetricType.create_analytics_end, null, ExitTo.developer);
              window.location.href = devReadyLink;
            } else {
              props.onContinue();
            }
          })
          .catch((soleErr) => {
            console.log(soleErr);
            setFailed(true);
            setErrorMessage(soleErr.data);
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch((err) => {
        console.log(err);
        setFailed(true);
        setLoading(false);
      });
  };

  return (
    <StepWrapper
      title="Which tables would you like to use in your dataset?"
      subtitle="Select the tables to include in your dataset"
      onBack={() => props.onBack()}
      onContinue={() => onContinue(false)}
      continueDisabled={
        (selectedDatabases.length === 0 && selectedSchemas.length === 0 && selectedSchemaLevelObjects.length === 0) ||
        loading
      }
      finishEarlyAvailable={true}
      finishEarlyContinueButtonText="Review tests"
      onFinishEarly={() => onContinue(true)}
    >
      <div className="w-full h-full flex flex-col items-center">
        <div className="flex w-full">
          <div className="w-1/2 h-full flex flex-col items-center">
            <div className="relative w-3/4 mb-3">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-400" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-2 text-sm text-gray-600">{snowflakeAccount}</span>
              </div>
            </div>
            <SourceSelector mode="selection" objectTypes={['table']} databases={[selectedDatabase]} />
          </div>
          <div className="w-1/2 h-full flex flex-col items-center">
            <div className="relative w-3/4 mb-3">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-400" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-2 text-sm text-gray-600">{dataProductName}</span>
              </div>
            </div>
            <SourceSelector mode="preview" objectTypes={['table']} databases={[selectedDatabase]} />
          </div>
        </div>
        <LoadingAndErrorSection isLoading={loading} isFailed={failed} errorMessage={errorMessage} />
      </div>
    </StepWrapper>
  );
}
